import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useCallback } from 'react'

import { KEYS } from 'core-components/helpers/eventKeys'
import { useFocus } from 'core-components/hooks/useFocus'
import { Button } from 'core-components/library/button'
import { DragNDrop } from 'core-components/library/dragNdrop'
import { Input } from 'core-components/library/inputs'
import { Loader } from 'core-components/library/loader'
import { NotificationController } from 'core-components/library/popup'
import { isEmpty, Validator } from 'core-components/library/validation'

import { FileNames } from './FileNames'
import { IDragNDropFileUploaderProps } from './interface/IDragNDropFileUploader'
import { IFileSettings } from './interface/IFileSettings'

import styles from './DragNDropFileUploader.module.scss'

export const DragNDropFileUploader = ({
  className,
  changeFileLoader,
  fileNames,
  maxFileSize = 30,
  urlInputVisibility = true,
  multifiles = false,
  acceptedFormats,
  disabled,
  required,
  content,
  rootStyleVisible = true,
  fileListVisibility = true,
  loading = false,
  onFileUploadError,
  textRender,
  onFileRemove,
  onRejectFiles,
}: IDragNDropFileUploaderProps) => {
  const [clicked, setClicked] = useState<number>(0)
  const [names, setNames] = useState(fileNames)
  const [url, setUrl] = useState('')
  const [showLoading, setShowLoading] = useState<boolean>(loading)
  const focus = useFocus(0)

  const isUploadRef = useRef<boolean>(false)

  useEffect(() => {
    isUploadRef.current = true
  }, [url])

  useEffect(() => {
    setNames(fileNames)
  }, [fileNames])

  useEffect(() => {
    setShowLoading(loading)
  }, [loading])

  const uploadFile = useCallback(
    (file: File) => {
      return new Promise((res: (file: IFileSettings) => void, rej: (error: string) => void) => {
        if (file.size / 1024 / 1024 >= maxFileSize) {
          const errorMessage = `The size of the uploaded file must not exceed ${maxFileSize} mb`
          rej(errorMessage)
          return NotificationController.showError({ message: errorMessage })
        }

        const reader = new FileReader()
        reader.onloadend = (e: any) => {
          const fileDetail: IFileSettings = { file: file, value: e.target.result }
          res(fileDetail)
        }
        reader.readAsDataURL(file)
      })
    },
    [maxFileSize]
  )

  const handleImageChange = useCallback(
    (file: File) => {
      setShowLoading(true)
      uploadFile(file)
        .then((result) => {
          setNames([result.file.name])
          changeFileLoader([result])
        })
        .catch((e: string) => {
          if (onFileUploadError) {
            onFileUploadError(e)
          }
        })
        .finally(() => setShowLoading(false))
    },
    [uploadFile, changeFileLoader, onFileUploadError]
  )

  const handleMultiImageChange = useCallback(
    (files: File[]) => {
      setShowLoading(true)
      const uploadPromises = Array.from(files).map((item) => {
        return uploadFile(item)
      })
      Promise.all(uploadPromises)
        .then((result) => {
          const names: string[] = []
          const fileLoaders: IFileSettings[] = []
          result.forEach((fileSettings) => {
            names.push(fileSettings.file.name)
            fileLoaders.push(fileSettings)
          })
          setNames(names)
          changeFileLoader(fileLoaders)
        })
        .catch((e: string) => {
          if (onFileUploadError) {
            onFileUploadError(e)
          }
        })
        .finally(() => setShowLoading(false))
    },
    [uploadFile, changeFileLoader, onFileUploadError]
  )

  const uploadUrl = () => {
    if (!!url && isUploadRef.current) {
      let fileName = url.split('/').pop() || ''
      if (!fileName.match(/[^\/]+\.[^\/]+$/)) {
        fileName = 'file.png'
      }
      setShowLoading(true)
      fetch(url).then((r) =>
        r.blob().then((blob) => {
          setNames([fileName])
          const file = new File([blob], fileName)
          uploadFile(file)
            .then((result) => {
              setNames([result.file.name])
              changeFileLoader([result])
            })
            .catch((e: string) => {
              if (onFileUploadError) {
                onFileUploadError(e)
              }
            })
            .finally(() => setShowLoading(false))
        })
      )
      isUploadRef.current = false
    }
  }

  const body = (
    <div
      data-testid={'dnd-wrapper'}
      className={classNames(styles.root, className || '')}
      onFocus={() => focus.onFocus()}
      onBlur={() => focus.onBlur()}
      onPaste={(evt) => {
        const file = evt.clipboardData?.files[0]
        if (focus.focused && file) {
          handleImageChange(file)
        }
      }}
      tabIndex={0}
    >
      <DragNDrop
        className={rootStyleVisible ? styles.container : styles.hideBorders}
        browseClick={clicked}
        uploadFile={(file) => {
          if (multifiles) {
            handleMultiImageChange(file)
          } else {
            const firstFile = file[0]
            handleImageChange(firstFile)
          }
        }}
        enablePointerEvents
        acceptedFormats={acceptedFormats}
        multifiles={multifiles}
        disabled={disabled}
        onRejectFiles={onRejectFiles}
      >
        {(content && content({ openFileList: () => setClicked((c) => c + 1) })) || (
          <>
            <div
              data-testid={'dnd-inner'}
              className={classNames(styles.inner, showLoading ? styles['inner--blur'] : '')}
            >
              {!names?.length && required && (
                <p className={styles.container__mandatory}>*Field is mandatory</p>
              )}
              <div className={styles.icon}>
                <svg
                  className={styles.icon__img}
                  viewBox="0 0 70 60"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M67.66.25H11.68a2.34 2.34 0 00-2.33 2.34v2.33H7a2.34 2.34 0 00-2.34 2.34V9.6H2.34A2.34 2.34 0 000 11.93v45.49a2.34 2.34 0 002.34 2.33h55.98a2.34 2.34 0 002.33-2.33v-2.34H63a2.34 2.34 0 002.34-2.34V50.4h2.33A2.34 2.34 0 0070 48.07V2.59c0-1.3-1.04-2.34-2.34-2.34zm-9.33 37.72l-6.11-7a1.17 1.17 0 00-1.76 0l-7.38 8.45-20.12-17.88c-.44-.4-1.1-.4-1.54 0L2.36 38.47V13.08c0-.64.52-1.16 1.17-1.16h53.64c.64 0 1.16.52 1.16 1.16v24.89zm3.51 14.76h-1.16V11.9a2.34 2.34 0 00-2.34-2.34H7.02V8.4c0-.65.52-1.16 1.17-1.16h53.64c.64 0 1.16.51 1.16 1.16L63 51.57c0 .64-.52 1.16-1.16 1.16zm4.66-4.68h-1.16V7.25A2.34 2.34 0 0063 4.9H11.68V3.75c0-.65.52-1.16 1.17-1.16h53.64c.64 0 1.16.51 1.16 1.16v43.14h.01c0 .64-.52 1.16-1.16 1.16z" />
                  <path d="M42.01 18.9a4.67 4.67 0 10.01 9.33 4.67 4.67 0 000-9.33z" />
                </svg>
              </div>
              <div className={styles.content}>
                <div className={styles.description}>
                  {(textRender && textRender('dndCommentText')) || (
                    <span>
                      Drag 'n Drop your attachments in here or click and press "Ctrl+V" or
                    </span>
                  )}

                  <Button
                    className={styles.description__button}
                    __testId={'dnd-browse-button'}
                    variant={'text'}
                    onClick={() => setClicked(clicked + 1)}
                    disabled={disabled}
                  >
                    {(textRender && textRender('dndBrowseWord')) || `Browse`}
                  </Button>
                </div>
                {urlInputVisibility ? (
                  <div className={styles.url} data-testid={'url-upload-input'}>
                    <div className={styles.url__text}>
                      {(textRender && textRender('dndOrWord')) || <span>Or</span>}
                    </div>
                    <Input
                      classes={{ root: styles.url__input }}
                      disabled={disabled}
                      value={url}
                      onChange={(value) => setUrl(value)}
                      onBlur={uploadUrl}
                      onKeyDown={(e) => {
                        if (e.key === KEYS.enter) {
                          uploadUrl()
                          e.preventDefault()
                        }
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <div
              data-testid={'dnd-loader-wrapper'}
              className={classNames(
                styles.loadingOverlay,
                showLoading ? styles['loadingOverlay--show'] : ''
              )}
            >
              <Loader />
            </div>
          </>
        )}
      </DragNDrop>
      <FileNames
        fileListVisibility={fileListVisibility}
        names={names}
        onFileRemove={onFileRemove}
      />
    </div>
  )
  return required ? (
    <Validator checkFn={isEmpty} value={names?.join(', ') || ''}>
      {body}
    </Validator>
  ) : (
    body
  )
}
